import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import useDriverGeoPing from "@/hooks/useDriverGeoPing";
import { ArrowRight, MapPin, Calendar, Boxes, Truck } from "lucide-react";
import { formatDate, packagingLabel } from "@/lib/utils";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function DriverTasks() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("today"); // today | all
  const navigate = useNavigate();
  useDriverGeoPing({ enabled: true });

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data));
  }, []);

  const active = useMemo(() => orders.filter((o) => !["zugestellt", "storniert"].includes(o.status)), [orders]);
  const done = useMemo(() => orders.filter((o) => o.status === "zugestellt"), [orders]);
  const today = todayISO();
  const visible = useMemo(
    () => (filter === "today" ? active.filter((o) => o.delivery_date === today) : active),
    [filter, active, today],
  );

  return (
    <Layout>
      <div className="px-4 md:px-10 py-4 md:py-8 max-w-3xl mx-auto pb-24">
        {/* Compact mobile header */}
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Fahrer-App</div>
            <h1 className="text-2xl md:text-4xl font-semibold tracking-tight mt-1">Meine Touren</h1>
          </div>
          <div className="text-right text-[11px] uppercase tracking-wider text-[var(--text-3)]">
            <div><span className="text-[#D4AF37] font-mono text-base">{active.length}</span> offen</div>
            <div><span className="font-mono">{done.length}</span> erledigt</div>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="mt-4 grid grid-cols-2 gap-1 p-1 border border-[var(--border)] rounded-sm bg-[var(--surface-2)]">
          <button
            data-testid="driver-filter-today"
            onClick={() => setFilter("today")}
            className={`py-2.5 text-xs uppercase tracking-wider rounded-sm transition-colors ${
              filter === "today" ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "text-[var(--text-2)] hover:text-[var(--text)]"
            }`}
          >
            Heute · {active.filter((o) => o.delivery_date === today).length}
          </button>
          <button
            data-testid="driver-filter-all"
            onClick={() => setFilter("all")}
            className={`py-2.5 text-xs uppercase tracking-wider rounded-sm transition-colors ${
              filter === "all" ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "text-[var(--text-2)] hover:text-[var(--text)]"
            }`}
          >
            Alle · {active.length}
          </button>
        </div>

        {/* Compact task list */}
        <div className="mt-4 space-y-2">
          {visible.length === 0 && (
            <div className="card p-8 text-center text-[var(--text-3)] text-sm" data-testid="driver-no-tasks">
              {filter === "today" ? "Heute keine Touren. ☕" : "Keine offenen Touren. Genießen Sie die Pause. ☕"}
            </div>
          )}
          {visible.map((o) => (
            <button
              key={o.id}
              onClick={() => navigate(`/driver/orders/${o.id}`)}
              data-testid={`driver-task-${o.order_number}`}
              className="card w-full p-3.5 text-left active:bg-[#D4AF37]/[0.04] hover:border-[#D4AF37]/40 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  {/* Pickup */}
                  <div className="flex items-center gap-1.5 text-xs text-[var(--text-3)] uppercase tracking-wider mb-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
                    <span>Abholung</span>
                  </div>
                  <div className="text-sm text-[var(--text-2)] truncate ml-[14px]">
                    {o.pickup?.zip} {o.pickup?.city} · {o.pickup?.name}
                  </div>
                  {/* Delivery */}
                  <div className="flex items-center gap-1.5 text-xs text-[var(--text-3)] uppercase tracking-wider mt-2 mb-1">
                    <MapPin size={11} strokeWidth={1.7} className="text-[#D4AF37]" />
                    <span>Lieferung</span>
                  </div>
                  <div className="text-base text-[var(--text)] truncate ml-[14px]">
                    <span className="font-medium">{o.delivery?.zip} {o.delivery?.city}</span>
                  </div>
                  <div className="text-sm text-[var(--text-2)] truncate ml-[14px]">
                    {o.delivery?.name}
                  </div>
                  {/* Line 2: badge + meta */}
                  <div className="flex flex-wrap items-center gap-2 mt-2 ml-[14px]">
                    <StatusBadge status={o.status} />
                    {o.status === "zwischenlager" && o.warehouse_location && (
                      <span className="text-[11px] text-orange-800 font-medium">{o.warehouse_location}</span>
                    )}
                    <span className="text-[11px] text-[var(--text-3)] flex items-center gap-1">
                      <Calendar size={11} strokeWidth={1.5} /> {formatDate(o.delivery_date)}
                    </span>
                    <span className="text-[11px] text-[var(--text-3)] flex items-center gap-1">
                      <Boxes size={11} strokeWidth={1.5} /> {o.pallet_count}
                    </span>
                    {o.positions?.length > 0 && (
                      <span className="text-[11px] text-[var(--text-2)] font-medium" data-testid={`driver-task-types-${o.order_number}`}>
                        {o.positions.slice(0, 2).map((p) => `${p.quantity}× ${packagingLabel(p.packaging_type)}`).join(" · ")}{o.positions.length > 2 ? " …" : ""}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="font-mono text-[11px] text-[var(--text-3)]">{o.order_number.split("-")[1]}</div>
                  <ArrowRight size={18} className="text-[#D4AF37]" />
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Erledigt — collapsed by default */}
        {done.length > 0 && filter === "all" && (
          <details className="mt-8" data-testid="driver-tasks-done-section">
            <summary className="cursor-pointer text-sm uppercase tracking-wider text-[var(--text-2)] hover:text-[#D4AF37] px-3 py-3 font-medium border border-[var(--border)] rounded-sm flex items-center justify-between">
              <span>Erledigte Touren</span>
              <span className="font-mono text-emerald-700 text-base">{done.length}</span>
            </summary>
            <div className="space-y-3 mt-4">
              {done.slice(0, 20).map((o) => (
                <Link
                  to={`/driver/orders/${o.id}`}
                  key={o.id}
                  className="card p-4 flex items-center justify-between hover:border-[var(--header)] transition-colors"
                  data-testid={`driver-done-${o.id}`}
                >
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="p-2 rounded-sm shrink-0" style={{ background: "rgba(34,197,94,0.10)" }}>
                      <Truck size={16} className="text-emerald-700" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-base font-medium text-[var(--text)] truncate">
                        {o.pickup?.city || "—"} → {o.delivery?.city || "—"}
                      </div>
                      {(o.delivery?.name) && (
                        <div className="text-xs text-[var(--text-2)] truncate mt-0.5">{o.delivery.name}</div>
                      )}
                      <div className="text-xs text-[var(--text-3)] flex items-center gap-3 mt-1.5">
                        <span className="font-mono">{o.order_number}</span>
                        <span>· {o.pallet_count} Pal.</span>
                        <span>· {formatDate(o.delivery_date)}</span>
                      </div>
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold ml-2 shrink-0">Zugestellt</span>
                </Link>
              ))}
            </div>
          </details>
        )}
      </div>
    </Layout>
  );
}
